import { useMemo, useState } from "react";
import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button, Form, Table } from "react-bootstrap";

import FeatureFlag from "./FeatureFlag";
import FeatureFlagListPagination from "./FeatureFlagListPagination";
import FeatureFlagListToolbar from "./FeatureFlagListToolbar";
import FormatFeatureFlagDate from "./FormatFeatureFlagDate";

/** Save/delete failure info for one row. */
export interface FeatureFlagRowError {
  message: string;
  /** True when the flag never existed on the server (a new item that failed to save). */
  isNew: boolean;
}

interface FeatureFlagListProps {
  featureFlags: FeatureFlag[];
  /** Rows to highlight as errored, keyed by flag id. */
  rowErrors: Map<number, FeatureFlagRowError>;
  /** Flag whose details panel is open; its row is highlighted. */
  selectedFlagId: number | null;
  /** Called when a row is clicked (outside its interactive controls). */
  onSelectFlag: (id: number) => void;
  onToggleEnabled: (id: number, enabled: boolean) => void;
  /** Reverts an existing errored row to its last saved values. */
  onResetFlag: (id: number) => void;
  /** Discards a new errored row that was never saved. */
  onRemoveFlag: (id: number) => void;
  onDeleteFlag: (id: number) => void;
}

const nameOrDescriptionFilter: FilterFn<FeatureFlag> = (row, _columnId, filterValue) => {
  const query = String(filterValue ?? "").trim().toLowerCase();
  if (!query) {
    return true;
  }

  const name = String(row.original.name ?? "").toLowerCase();
  const description = String(row.original.description ?? "").toLowerCase();
  return name.includes(query) || description.includes(query);
};

/**
 * Returns true when the click landed on an interactive control (switch,
 * button, link) whose own handler should win over row selection.
 */
function isInteractiveClick(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("button, input, label, a, select") !== null
  );
}

/** Feature flag list table with search, sort, and pagination. */
function FeatureFlagList({
  featureFlags,
  rowErrors,
  selectedFlagId,
  onSelectFlag,
  onToggleEnabled,
  onResetFlag,
  onRemoveFlag,
  onDeleteFlag,
}: FeatureFlagListProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const columns = useMemo<ColumnDef<FeatureFlag>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        enableGlobalFilter: true,
      },
      {
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        enableGlobalFilter: true,
        cell: ({ getValue }) => (
          <span className="truncatedDescription" title={String(getValue() ?? "")}>
            {String(getValue() ?? "")}
          </span>
        ),
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) => {
          const rowADate = rowA.getValue<Date | null>(columnId);
          const rowBDate = rowB.getValue<Date | null>(columnId);
          const rowATimestamp = rowADate ? rowADate.getTime() : Number.NEGATIVE_INFINITY;
          const rowBTimestamp = rowBDate ? rowBDate.getTime() : Number.NEGATIVE_INFINITY;
          return rowATimestamp === rowBTimestamp ? 0 : rowATimestamp > rowBTimestamp ? 1 : -1;
        },
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) => {
          const rowADate = rowA.getValue<Date | null>(columnId);
          const rowBDate = rowB.getValue<Date | null>(columnId);
          const rowATimestamp = rowADate ? rowADate.getTime() : Number.NEGATIVE_INFINITY;
          const rowBTimestamp = rowBDate ? rowBDate.getTime() : Number.NEGATIVE_INFINITY;
          return rowATimestamp === rowBTimestamp ? 0 : rowATimestamp > rowBTimestamp ? 1 : -1;
        },
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        accessorKey: "enabled",
        header: "Enabled",
        enableSorting: true,
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <Form.Check
            type="switch"
            id={`feature-flag-enabled-${row.original.id}`}
            className="mb-0 enabledSwitch"
            checked={row.original.enabled}
            onChange={(e) => onToggleEnabled(row.original.id, e.target.checked)}
            aria-label={`Enabled status for ${row.original.name}`}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => {
          const error = rowErrors.get(row.original.id);

          return (
            <div className="rowActions">
              <div className="rowActionButtons">
                <Button variant="secondary" size="sm" type="button">
                  Edit
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  className="text-danger deleteButton"
                  aria-label="Delete feature flag"
                  onClick={() => onDeleteFlag(row.original.id)}
                >
                  <i className="fa fa-trash" />
                </Button>
              </div>
              {error && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  type="button"
                  className="errorActionButton"
                  title={error.message}
                  onClick={() =>
                    error.isNew ? onRemoveFlag(row.original.id) : onResetFlag(row.original.id)
                  }
                >
                  {error.isNew ? "Remove" : "Reset"}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [onToggleEnabled, rowErrors, onResetFlag, onRemoveFlag, onDeleteFlag]
  );

  const table = useReactTable({
    data: featureFlags,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: nameOrDescriptionFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="featureFlagList">
      <FeatureFlagListToolbar
        pageSize={pagination.pageSize}
        searchValue={globalFilter}
        onPageSizeChange={(pageSize) =>
          setPagination({ pageIndex: 0, pageSize })
        }
        onSearchChange={(value) => {
          setGlobalFilter(value);
          setPagination((current) => ({ ...current, pageIndex: 0 }));
        }}
      />

      <Table responsive className="featureFlagTable table-striped mb-0">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    className={canSort ? "sortableHeader" : undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      sorted === "asc"
                        ? "ascending"
                        : sorted === "desc"
                          ? "descending"
                          : canSort
                            ? "none"
                            : undefined
                    }
                  >
                    <span className="headerContent">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <span className="sortIcons" aria-hidden="true">
                          <i className={`fa fa-caret-up${sorted === "asc" ? " is-active" : ""}`} />
                          <i className={`fa fa-caret-down${sorted === "desc" ? " is-active" : ""}`} />
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted py-4">
                No feature flags found.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const classNames = ["selectableRow"];
              if (rowErrors.has(row.original.id)) {
                classNames.push("flagRowError");
              }
              if (row.original.id === selectedFlagId) {
                classNames.push("selectedRow");
              }

              return (
                <tr
                  key={row.id}
                  className={classNames.join(" ")}
                  onClick={(e) => {
                    if (!isInteractiveClick(e.target)) {
                      onSelectFlag(row.original.id);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <FeatureFlagListPagination
        pageIndex={pagination.pageIndex}
        pageCount={table.getPageCount()}
        pageSize={pagination.pageSize}
        filteredRowCount={filteredRowCount}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
        onGoToPage={(page) => table.setPageIndex(page)}
      />
    </div>
  );
}

export default FeatureFlagList;
